import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anon, object = user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let oauthTimer = null;
    let hasResolved = false;
    let hasRedirected = false;

    // Detect OAuth callback in both query params (PKCE) and hash (implicit fallback)
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isRecoveryFlow = hashParams.get("type") === "recovery";
    const isOAuthCallback =
      !isRecoveryFlow &&
      ((searchParams.has("code") && searchParams.has("state")) ||
       hashParams.has("access_token") ||
       hashParams.has("error"));

    // ── Recovery flow: completely skip auth initialization ─────────────────
    // Recovery tokens are NOT valid session tokens. Sending them to /api/auth/me
    // causes 403 "session_not_found". Let ResetPassword.jsx handle everything.
    if (isRecoveryFlow) {
      console.log("[AUTH] Recovery flow detected — skipping auth initialization");
      setLoading(false);
      return () => { active = false; };
    }

    const resolve = (userValue) => {
      if (!active) return;
      hasResolved = true;
      setUser(userValue);
      setLoading(false);
      if (oauthTimer) clearTimeout(oauthTimer);

      // Clean hash from URL after OAuth processing to avoid /# lingering
      if (window.location.hash && isOAuthCallback) {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search
        );
      }

      // Redirect authenticated users after OAuth callback
      // (Handles cases where Supabase redirects to root instead of /login)
      if (userValue && userValue.id && isOAuthCallback && !hasRedirected) {
        hasRedirected = true;
        const target = userValue.is_admin ? "/admin" : "/dashboard";
        console.log(`[AUTH] OAuth callback: redirecting to ${target}`);
        window.location.replace(target);
      }
    };

    // Set up listener FIRST so it's ready before getSession triggers any events
    const { data: listener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        console.log(
          `[AUTH] onAuthStateChange: event=${_event}, hasSession=${!!session}`
        );
        if (session?.access_token) {
          const profile = await fetchProfile(session.access_token);
          if (active) resolve(profile);
        } else if (!isOAuthCallback) {
          // Only resolve to false outside of OAuth callback
          if (active) resolve(false);
        }
      }
    );

    // Get initial session (only resolve immediately if NOT an OAuth callback)
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!active) return;

      if (session?.access_token) {
        // If we already resolved via onAuthStateChange, don't overwrite
        if (!hasResolved) {
          const profile = await fetchProfile(session.access_token);
          if (active && !hasResolved) resolve(profile);
        }
      } else if (!isOAuthCallback) {
        // No session and not an OAuth callback → definitely logged out
        if (!hasResolved) resolve(false);
      }
      // If isOAuthCallback and session is null: wait for onAuthStateChange
    })();

    // Generous timeout for OAuth (network + code exchange can take time)
    if (isOAuthCallback) {
      console.log("[AUTH] OAuth callback detected, waiting for session...");
      oauthTimer = setTimeout(() => {
        if (!hasResolved) {
          console.error("[AUTH] OAuth callback timed out after 30s");
          resolve(false);
        }
      }, 30000);
    }

    return () => {
      active = false;
      if (oauthTimer) clearTimeout(oauthTimer);
      listener?.subscription.unsubscribe();
    };
  }, []);

  async function fetchProfile(accessToken) {
    console.log("[AUTH] fetchProfile: calling /api/auth/me...");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      console.log(`[AUTH] fetchProfile: response status=${res.status}`);
      if (!res.ok) {
        const errText = await res.text();
        console.error(`[AUTH] fetchProfile: FAILED status=${res.status}, body=${errText}`);
        return false;
      }
      const data = await res.json();
      console.log(`[AUTH] fetchProfile: SUCCESS user_id=${data.id}, email=${data.email}`);
      // Store token for API interceptor
      localStorage.setItem("rs_token", accessToken);
      return data;
    } catch (e) {
      console.error("[AUTH] fetchProfile: NETWORK ERROR", e);
      return false;
    }
  }

  const login = async (email, password) => {
    console.log(`[AUTH] login: attempting for email=${email}`);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        // ── Legacy fallback: user registered before Supabase Auth migration ──
        // Supabase auth errors include status 400 with messages like:
        // "Invalid login credentials", "Email not confirmed", "User not found", etc.
        // For ANY Supabase auth error, try the old backend login system.
        const isAuthError = !error.message?.includes("network") && 
                            !error.message?.includes("timeout") &&
                            !error.message?.includes("failed to fetch");
        console.log(`[AUTH] login: Supabase error — "${error.message}", isAuthError=${isAuthError}`);
        
        if (isAuthError) {
          console.log("[AUTH] login: trying legacy backend login...");
          const legacyRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
            credentials: "include",
          });
          console.log(`[AUTH] login: legacy response status=${legacyRes.status}`);
          if (!legacyRes.ok) {
            const errData = await legacyRes.json().catch(() => ({}));
            console.error(`[AUTH] login: legacy FAILED — ${errData.detail || legacyRes.statusText}`);
            throw new Error(errData.detail || "Invalid email or password");
          }
          const legacyData = await legacyRes.json();
          console.log("[AUTH] login: legacy login succeeded");
          
          const profile = await fetchProfile(legacyData.token);
          if (!profile) {
            console.error("[AUTH] login: FAILED — profile fetch failed after legacy login");
            return { ok: false, error: "Could not load user profile. Please try again." };
          }
          setUser(profile);
          console.log(`[AUTH] login: complete via legacy, profile_found=true`);
          return { ok: true, user: profile };
        }
        console.error(`[AUTH] login: non-auth Supabase error — ${error.message}`);
        throw error;
      }
      console.log("[AUTH] login: Supabase signInWithPassword succeeded");

      const profile = await fetchProfile(data.session.access_token);
      if (!profile) {
        console.error("[AUTH] login: FAILED — profile fetch failed (backend error)");
        await supabase.auth.signOut();
        return { ok: false, error: "Could not load user profile. Please try again." };
      }
      setUser(profile);
      console.log(`[AUTH] login: complete, profile_found=true`);
      return { ok: true, user: profile };
    } catch (e) {
      console.error(`[AUTH] login: FAILED — ${e.message || e}`);
      return { ok: false, error: e.message || "Login failed" };
    }
  };

  const register = async ({ email, password, name, businessName }) => {
    console.log(`[AUTH] register: attempting for email=${email}`);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            business_name: businessName,
          },
        },
      });
      if (error) {
        console.error(`[AUTH] register: Supabase error — ${error.message}`);
        throw error;
      }
      console.log(`[AUTH] register: Supabase signUp succeeded, has_session=${!!data.session}`);

      // If auto-confirmed (or user already exists), we get a session
      if (data.session) {
        const profile = await fetchProfile(data.session.access_token);
        if (!profile) {
          console.error("[AUTH] register: FAILED — profile fetch failed after sign-up");
          await supabase.auth.signOut();
          return { ok: false, error: "Account created but profile load failed. Please log in." };
        }
        setUser(profile);
        console.log(`[AUTH] register: auto-logged in, profile_found=true`);
        return { ok: true };
      }
      // Otherwise email confirmation is required
      console.log("[AUTH] register: email confirmation required");
      return { ok: true, requiresConfirmation: true };
    } catch (e) {
      console.error(`[AUTH] register: FAILED — ${e.message || e}`);
      return { ok: false, error: e.message || "Registration failed" };
    }
  };

  const signInWithGoogle = async () => {
    console.log("[AUTH] signInWithGoogle: initiating OAuth...");
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/login`,
        },
      });
      if (error) {
        console.error(`[AUTH] signInWithGoogle: Supabase error — ${error.message}`);
        throw error;
      }
      console.log(`[AUTH] signInWithGoogle: redirect URL=${data.url}`);
      // OAuth opens a redirect — the onAuthStateChange listener handles the result
      return { ok: true, url: data.url };
    } catch (e) {
      console.error(`[AUTH] signInWithGoogle: FAILED — ${e.message || e}`);
      return { ok: false, error: e.message || "Google sign-in failed" };
    }
  };

  const forgotPassword = async (email) => {
    console.log(`[AUTH] forgotPassword: requesting reset for ${email}`);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        console.error(`[AUTH] forgotPassword: Supabase error — ${error.message}`);
        throw error;
      }
      console.log("[AUTH] forgotPassword: reset email sent");
      return { ok: true };
    } catch (e) {
      console.error(`[AUTH] forgotPassword: FAILED — ${e.message || e}`);
      return { ok: false, error: e.message || "Failed to send reset email" };
    }
  };

  const updatePassword = async (newPassword) => {
    console.log("[AUTH] updatePassword: updating...");
    try {
      // Set recovery session from URL hash if present
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const recoveryToken = hashParams.get("access_token");
      if (recoveryToken) {
        console.log("[AUTH] updatePassword: setting recovery session from URL hash");
        const { error: setSessionErr } = await supabase.auth.setSession({
          access_token: recoveryToken,
          refresh_token: hashParams.get("refresh_token") || "",
        });
        if (setSessionErr) {
          console.error(`[AUTH] updatePassword: setSession error — ${setSessionErr.message}`);
          // Continue anyway — Supabase may have already parsed the hash
        }
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) {
        console.error(`[AUTH] updatePassword: Supabase error — ${error.message}`);
        throw error;
      }
      console.log("[AUTH] updatePassword: SUCCESS");

      // Clear recovery session and stale tokens after password change
      // (Password change invalidates all existing sessions)
      await supabase.auth.signOut();
      localStorage.removeItem("rs_token");
      localStorage.removeItem("sb-" + process.env.REACT_APP_SUPABASE_URL?.replace("https://", "") + "-auth-token");
      console.log("[AUTH] updatePassword: cleared auth state after password change");
      
      return { ok: true };
    } catch (e) {
      console.error(`[AUTH] updatePassword: FAILED — ${e.message || e}`);
      return { ok: false, error: e.message || "Failed to update password" };
    }
  };

  const logout = async () => {
    console.log("[AUTH] logout: signing out...");
    try {
      await supabase.auth.signOut();
      console.log("[AUTH] logout: Supabase signOut succeeded");
    } catch (e) {
      console.error("[AUTH] logout: error", e);
    }
    localStorage.removeItem("rs_token");
    setUser(false);
    console.log("[AUTH] logout: complete");
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        login,
        register,
        signInWithGoogle,
        forgotPassword,
        updatePassword,
        logout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);